import React, { useCallback, useEffect, useState } from 'react'
import BkPixel, { BK_INK } from './BkPixel'
import { stitchCode, stitchLines, stitchText, type PixelRows } from './pixelType'
import { BK_WORD_REDEEM } from './lettering'
import { tornPaper } from './paper'
import { BK_ASSET } from './layout'

const MAX_LENGTH = 16

const ROWS = ['1234567890', 'QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM']

/** A left arrow, for delete. */
const DELETE_GLYPH: PixelRows = [
  '...#.....',
  '..##.....',
  '.########',
  '#########',
  '.########',
  '..##.....',
  '...#.....'
]

/** The sheet, in 1920x1080 design pixels. */
const SHEET = { left: 410, top: 96, width: 1100, height: 760 }
const KEY_W = 84
const KEY_H = 74
const KEY_GAP = 12

/**
 * The voucher keypad, in the plates' vocabulary: a torn paper sheet with a
 * stitched border, their own "redeem" lettering for a title, paper keys set
 * in block capitals, and their "confirm" swan — the plate's, letters and all
 * — to submit. The swan straddles the sheet's edge the way the plate swans
 * straddle the gold frames.
 *
 * It replaces the stock OnScreenKeyboard on this route only, with the same
 * contract (`onEnter`, `onClose`, `isLoading`, `error`). A physical keyboard
 * types into it too, which is how an operator tests it at a desk.
 */
const BkVoucherPad: React.FC<{
  isLoading: boolean
  error: string
  onInputChange: () => void
  onEnter: (code: string) => void
  onClose: () => void
}> = ({ isLoading, error, onInputChange, onEnter, onClose }) => {
  const [code, setCode] = useState('')

  const type = useCallback(
    (c: string): void => {
      if (isLoading) return
      setCode((prev) => (prev.length >= MAX_LENGTH ? prev : prev + c))
      onInputChange()
    },
    [isLoading, onInputChange]
  )
  const erase = useCallback((): void => {
    if (isLoading) return
    setCode((prev) => prev.slice(0, -1))
    onInputChange()
  }, [isLoading, onInputChange])
  const submit = useCallback((): void => {
    if (!isLoading && code) onEnter(code)
  }, [code, isLoading, onEnter])

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (/^[a-z0-9]$/i.test(e.key)) type(e.key.toUpperCase())
      else if (e.key === 'Backspace') erase()
      else if (e.key === 'Enter') submit()
      else if (e.key === 'Escape') onClose()
      else return
      e.preventDefault()
    }
    window.addEventListener('keydown', onKey)
    return (): void => window.removeEventListener('keydown', onKey)
  }, [erase, onClose, submit, type])

  const message = isLoading ? 'Checking...' : error
  const keyRow = (keys: string[], extra?: React.ReactNode): React.ReactNode => (
    <div className="flex justify-center" style={{ gap: KEY_GAP }}>
      {keys.map((k) => (
        <PaperKey key={k} label={k} onPress={() => type(k)} disabled={isLoading}>
          <BkPixel rows={stitchCode(k)} block={5} />
        </PaperKey>
      ))}
      {extra}
    </div>
  )

  return (
    <div className="absolute inset-0 z-50 bg-[rgba(45,12,12,0.55)]" role="dialog" aria-modal>
      <div
        className="absolute"
        style={{
          ...SHEET,
          filter: 'drop-shadow(3px 10px 14px rgba(40, 10, 5, 0.45))'
        }}
      >
        <svg
          width={SHEET.width}
          height={SHEET.height}
          viewBox={`0 0 ${SHEET.width} ${SHEET.height}`}
          className="absolute inset-0"
          aria-hidden
        >
          <defs>
            <linearGradient id="bk-sheet" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#FFFDFB" />
              <stop offset="1" stopColor="#F1ECE5" />
            </linearGradient>
          </defs>
          <path d={tornPaper(SHEET.width, SHEET.height, 7)} fill="url(#bk-sheet)" />
          <rect
            x={22}
            y={22}
            width={SHEET.width - 44}
            height={SHEET.height - 44}
            fill="none"
            stroke={BK_INK}
            strokeWidth={3}
            strokeDasharray="10 7"
          />
        </svg>

        {/* Close: a small stitched square in the corner. z-10 because the
            content column below is positioned and spans the whole sheet — it
            painted over this button and took every tap meant for it. */}
        <button
          aria-label="Close"
          onClick={onClose}
          className="absolute z-10 flex items-center justify-center bg-transparent"
          style={{ right: 36, top: 36, width: 64, height: 64 }}
        >
          <BkPixel rows={stitchText('X')} block={6} />
        </button>

        <div className="relative flex h-full flex-col items-center" style={{ paddingTop: 44 }}>
          <BkPixel rows={BK_WORD_REDEEM} block={1.5} />

          {/* The code, stitched onto a dashed field. */}
          <div
            className="mt-6 flex items-center justify-center"
            style={{
              width: 820,
              height: 96,
              borderBottom: `4px dashed ${BK_INK}`
            }}
            aria-live="polite"
            aria-label={code ? `Code ${code.split('').join(' ')}` : 'No code yet'}
          >
            {code ? (
              <BkPixel rows={stitchCode(code)} block={7} />
            ) : (
              <span className="animate-pulse">
                <BkPixel rows={['#####']} block={7} color="rgba(124,39,20,0.35)" />
              </span>
            )}
          </div>

          <div className="flex h-[58px] flex-col items-center justify-center gap-1">
            {message &&
              stitchLines(message, 150).map((rows, i) => (
                <BkPixel key={i} rows={rows} block={3} color={isLoading ? BK_INK : '#B3202E'} />
              ))}
          </div>

          <div className="flex flex-col" style={{ gap: KEY_GAP }}>
            {keyRow(ROWS[0].split(''))}
            {keyRow(ROWS[1].split(''))}
            {keyRow(
              ROWS[2].split(''),
              <PaperKey label="Delete" onPress={erase} disabled={isLoading}>
                <BkPixel rows={DELETE_GLYPH} block={5} />
              </PaperKey>
            )}
            {keyRow(ROWS[3].split(''))}
          </div>
        </div>

        {/* Their own confirm swan submits. */}
        <button
          aria-label="Redeem"
          onClick={submit}
          disabled={isLoading || !code}
          className="absolute cursor-pointer bg-transparent p-0 transition-opacity disabled:cursor-default disabled:opacity-50"
          style={{ left: SHEET.width / 2 - 137, top: SHEET.height - 100, width: 273, height: 179 }}
        >
          <img src={BK_ASSET('confirm-swan.webp')} alt="" aria-hidden className="h-full w-full" />
        </button>
      </div>
    </div>
  )
}

/** One key: a scrap of paper that inks in while pressed. */
const PaperKey: React.FC<{
  label: string
  onPress: () => void
  disabled?: boolean
  children: React.ReactNode
}> = ({ label, onPress, disabled, children }) => (
  <button
    aria-label={label}
    onClick={onPress}
    disabled={disabled}
    className="group flex items-center justify-center rounded-[3px] bg-[#FBF7F1] shadow-[1px_3px_4px_rgba(60,20,10,0.25)] ring-1 ring-[rgba(124,39,20,0.18)] active:bg-[#7C2714] disabled:opacity-60 [&:active_path]:fill-[#FFFAF9]"
    style={{ width: KEY_W, height: KEY_H }}
  >
    {children}
  </button>
)

export default BkVoucherPad
